import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Modal, Form, Input, Typography, App, Space, Switch, Select, Breadcrumb, Tooltip, theme, Card, Tag, Row, Col } from "antd";
import { EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthProvider";
import ForbiddenPage from "../Status/ForbiddenPage";
import { getBackendUrl } from '../../utils/env';
import axios from "axios";
import dayjs from 'dayjs';
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es"; // Opcional para español

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Title, Text } = Typography;
const { Option } = Select;

export default function AdminUsuariosDashboard() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [selectedGender, setSelectedGender] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");


  const filteredUsuarios = useMemo(() => {
    return usuarios.filter((u) => {
      // 1. Text Search Filter (Name, Last Names, Dinamica)
      const fullName = `${u.name} ${u.ape_pat} ${u.ape_mat} ${u.dinamica}`.toLowerCase();
      const matchesText = fullName.includes(searchText.toLowerCase());

      // 2. Gender Dropdown Filter
      // If no gender is selected, matchesGender is always true
      const matchesGender = selectedGender
        ? u.gender === selectedGender
        : true;

      return matchesText && matchesGender;
    });
  }, [searchText, selectedGender, usuarios]);


  // 1. Get unique genders for the Select options (do this once)
  const genderOptions = useMemo(() => {
    const unique = [...new Set(usuarios.map(u => u.gender))];
    return unique.filter(Boolean); // Removes null/undefined if any exist
  }, [usuarios]);




  useEffect(() => {
    if (user?.id === 1) fetchUsuarios();
  }, [user]);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${getBackendUrl()}api/admin/users.php`);
      if (data.success) {
        setUsuarios(data.users);
      } else {
        message.error("No se pudieron cargar los usuarios");
      }
    } catch (error) {
      message.error("Error al cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };
  const sendAction = async (payload, successMessage) => {
    try {
      const { data } = await axios.post(
        `${getBackendUrl()}api/admin/users.php?id=${payload.id}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      if (data.success) {
        message.success(successMessage);
        fetchUsuarios();
      } else {
        message.error(data.message || "Error en la operación");
      }
    } catch (error) {
      message.error("Error en la operación");
    }
  };


  const handleToggleActive = (record) => {
    sendAction({ action: "toggle", id: record.id, is_active: record.is_active ? 0 : 1 }, "Usuario actualizado");
  };

  const openModal = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      sacraments: user.sacraments ? user.sacraments.split(",") : []
    });
    setModalVisible(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        sacraments: values.sacraments.join(",")
      };
      const { data } = await axios.post(
        `${getBackendUrl()}api/admin/users.php?id=${editingUser.id}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      if (data.success) {
        message.success("Usuario actualizado");
        setModalVisible(false);
        fetchUsuarios();
      } else {
        message.error("Error al actualizar usuario");
      }
    } catch (error) {
      message.error("Error al actualizar usuario");
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }

    return edad;
  };

  if (user?.id !== 1) return <ForbiddenPage />;

  const columns = [
    {
      title: "Foto",
      dataIndex: "profile_picture",
      key: "profile_picture",
      render: (url, record) => {
        const fullUrl = url ? `${getBackendUrl()}${url}` : "/default-avatar.png";
        return (
          <Tooltip title="Ver imagen">
            <img
              src={fullUrl}
              alt="avatar"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                cursor: "pointer",
                boxShadow: "0 0 4px rgba(0,0,0,0.1)"
              }}
              onClick={() => {
                setPreviewImage(fullUrl);
                setPreviewVisible(true);
              }}
            />
          </Tooltip>
        );
      }
    },

    { title: "Nombre", dataIndex: "name", key: "name" },
    { title: "Apellido", dataIndex: "ape_pat", key: "ape_pat" },
    { title: "Correo", dataIndex: "email", key: "email" },
    { title: "Genero", dataIndex: "gender", key: "gender" },
    {
      title: "Dinamica", dataIndex: "dinamica", key: "dinamica",
      render: (dinamica) => dinamica ? dinamica : "Sin datos"
    },
    {
      title: "Fecha de Nacimiento",
      dataIndex: "birth_date",
      key: "birth_date",
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : "N/A"
    },
    {
      title: "Edad",
      dataIndex: "birth_date",
      key: "edad",
      render: (date) => date ? calcularEdad(date) : "N/A"
    },
    {
      title: "Sacramentos",
      dataIndex: "sacraments",
      key: "sacraments",
      render: (sacraments) => sacraments ? sacraments.split(",").join(", ") : "Ninguno"
    },
    {
      title: "Bravos",
      dataIndex: "points",
      key: "points",
      render: (points) => points || 0
    },
    {
      title: "Activo",
      dataIndex: "is_active",
      key: "is_active",
      render: (is_active, record) => (
        <Switch checked={!!is_active} onChange={() => handleToggleActive(record)} checkedChildren="Sí"
          unCheckedChildren="No" />
      ),
    },
    {
      title: "Último acceso",
      dataIndex: "last_login",
      key: "last_login",
      render: (date) => {
        if (!date) return "Nunca";

        const formatted = dayjs(date).format("DD/MM/YYYY HH:mm");
        const relative = dayjs(date).fromNow();

        return (
          <Tooltip title={formatted}>
            <span>{relative}</span>
          </Tooltip>
        );
      }


    },

    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar Usuario" color="blue">
            <Button icon={<EditOutlined />} onClick={() => openModal(record)}></Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{
      padding: 'clamp(16px, 4vw, 32px)',
      maxWidth: 1400, // Un poco más ancho para tablas complejas
      margin: '0 auto',
      backgroundColor: token.colorBgLayout, // Fondo sutil para que el Card resalte
      minHeight: '100vh'
    }}>

      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/admin")}>Panel de Administración</a> },
          { title: "Gestión de Dinámicos" },
        ]}
      />

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0, color: token.colorText }}>Gestión de Dinámicos</Title>
          <Text type="secondary">Administra los datos de los dinámicos registrados.</Text>
        </div>
      </div>

      <Card borderless shadow style={{ borderRadius: token.borderRadiusLG, boxShadow: token.boxShadowTertiary, border: `1px solid ${token.colorBorderSecondary}` }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
              placeholder="Buscar por nombre, apellido..."
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </Col>
          {/* Aquí podrías añadir un Select para filtrar por Sacramento rápidamente */}
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filtrar por Género"
              allowClear
              style={{ flex: 1 }}
              onChange={(value) => setSelectedGender(value)}
            >
              {/* Map through unique genders from your raw data */}
              {[...new Set(usuarios.map(u => u.gender))].map(gender => (
                <Option key={gender} value={gender}>{gender}</Option>
              ))}
            </Select>
          </Col>

        </Row>

        <Table
          dataSource={filteredUsuarios}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          size="middle"
          locale={{
            emptyText: searchText
              ? "No hay dinámicos que coincidan con tu búsqueda"
              : "No hay dinámicos registrados en Sentinel"
          }}
          pagination={{
            pageSize: 10,
            showTotal: (total) => {
              if (total === 0) return "Ningún dinámico encontrado";
              if (total === 1) return `Total: ${total} dinámico`;
              return `Total: ${total} dinámicos`;
            },
          }}
        />
        <Modal

          title="Editar Usuario"

          open={modalVisible}

          onCancel={() => setModalVisible(false)}

          onOk={handleModalSubmit}

          okText="Guardar"

          cancelText="Cancelar"

        >

          <Form form={form} layout="vertical">

            <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>

              <Input placeholder="Nombre completo" allowClear />

            </Form.Item>

            <Form.Item name="email" label="Correo" rules={[{ required: true, type: "email" }]}>

              <Input placeholder="usuario@correo.com" allowClear />

            </Form.Item>

            <Form.Item name="sacraments" label="Sacramentos">

              <Select mode="multiple" placeholder="Selecciona sacramentos" allowClear>

                <Option value="bautismo">Bautismo</Option>

                <Option value="eucaristia">Primera Comunión</Option>

                <Option value="confirmacion">Confirmación</Option>

              </Select>

            </Form.Item>

          </Form>

        </Modal>



      </Card>
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
      >
        <img
          alt="preview"
          src={previewImage}
          style={{
            width: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: 8
          }}
        />
      </Modal>
    </div>
  );
}
